import { z } from 'zod';
import { isUnknownArray, isUnknownRecord as isRecord } from '../validation/unknown-record';

/**
 * RFC 6901 JSON Pointer test. A pointer is the empty string (whole document) or
 * a sequence of `/`-prefixed reference tokens. Within a token, `~` is only
 * legal as part of the escapes `~0` and `~1`.
 */
export function isJsonPointer(value: string): boolean {
  if (value === '') return true;
  if (value[0] !== '/') return false;
  return !/~(?![01])/.test(value);
}

export function parseJsonPointer(path: string): string[] {
  if (!isJsonPointer(path)) {
    throw new Error(`Invalid JSON Pointer: ${path}`);
  }

  if (path === '') return [];

  return path
    .slice(1)
    .split('/')
    .map((segment) => segment.replace(/~1/g, '/').replace(/~0/g, '~'));
}

export function getJsonPointerValue(source: unknown, path: string): unknown {
  if (!isJsonPointer(path)) return undefined;

  if (isRecord(source) && Object.prototype.hasOwnProperty.call(source, path)) {
    return source[path];
  }

  let current = source;
  for (const segment of parseJsonPointer(path)) {
    if (Array.isArray(current)) {
      const index = Number(segment);
      if (!Number.isInteger(index) || index < 0 || String(index) !== segment) return undefined;
      current = current[index];
      continue;
    }

    if (!isRecord(current) || !Object.prototype.hasOwnProperty.call(current, segment)) {
      return undefined;
    }

    current = current[segment];
  }

  return current;
}

export function setJsonPointerValue(source: unknown, path: string, value: unknown): unknown {
  const segments = parseJsonPointer(path);
  if (segments.length === 0) return value;

  const root = cloneContainer(source, segments[0]);
  let current: Record<string, unknown> | unknown[] = root;

  for (let index = 0; index < segments.length - 1; index += 1) {
    const segment = segments[index];
    const nextSegment = segments[index + 1];
    const existing = readContainerValue(current, segment);
    const next = cloneContainer(existing, nextSegment);

    writeContainerValue(current, segment, next);
    current = next;
  }

  writeContainerValue(current, segments[segments.length - 1], value);
  return root;
}

export type JsonValue =
  | null
  | string
  | number
  | boolean
  | JsonValue[]
  | { [key: string]: JsonValue };

export function isJsonSerializableValue(value: unknown): value is JsonValue {
  return isJsonValue(value, new WeakSet<object>());
}

export const jsonPointerSchema = z.string().refine(isJsonPointer, {
  message: 'path must be an RFC 6901 JSON Pointer',
});

export const nonEmptyJsonPointerSchema = jsonPointerSchema.refine((value) => value.length > 0, {
  message: 'path must target a location',
});

export const jsonValueSchema = z
  .custom<JsonValue>(isJsonSerializableValue, {
    message: 'value must be JSON-serializable',
  })
  .meta({ type: ['string', 'number', 'boolean', 'object', 'array', 'null'] });

function isJsonValue(value: unknown, seen: WeakSet<object>): value is JsonValue {
  if (value === null) return true;

  switch (typeof value) {
    case 'string':
    case 'boolean':
      return true;
    case 'number':
      return Number.isFinite(value);
    case 'object':
      break;
    default:
      return false;
  }

  if (seen.has(value)) return false;
  seen.add(value);

  if (isUnknownArray(value)) {
    const valid = value.every((item) => isJsonValue(item, seen));
    seen.delete(value);
    return valid;
  }

  if (!isRecord(value)) return false;
  const prototype = Reflect.getPrototypeOf(value);
  if (prototype !== Object.prototype && prototype !== null) {
    seen.delete(value);
    return false;
  }

  const valid = Object.values(value).every((item) => isJsonValue(item, seen));
  seen.delete(value);
  return valid;
}

function cloneContainer(value: unknown, nextSegment: string): Record<string, unknown> | unknown[] {
  if (isUnknownArray(value)) return value.slice();
  if (isRecord(value)) return { ...value };
  return isArrayIndex(nextSegment) ? [] : {};
}

function readContainerValue(
  container: Record<string, unknown> | unknown[],
  segment: string,
): unknown {
  if (isUnknownArray(container)) {
    return isArrayIndex(segment) ? container[Number(segment)] : undefined;
  }

  return container[segment];
}

function writeContainerValue(
  container: Record<string, unknown> | unknown[],
  segment: string,
  value: unknown,
): void {
  if (isUnknownArray(container) && isArrayIndex(segment)) {
    container[Number(segment)] = value;
    return;
  }

  Reflect.set(container, segment, value);
}

function isArrayIndex(value: string): boolean {
  if (value === '') return false;
  const index = Number(value);
  return Number.isInteger(index) && index >= 0 && String(index) === value;
}
